import { useParams } from 'react-router-dom';
import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import useDbDebugV2 from '../components/report/blocks/julkisivuV2/services/useDbDebugV2';

// Standalone read-only DB viewer, opened in its own tab from the "🔍 DB-taulut"
// button in KuntoarviovalikkoBlockV2. Ports the actual canvas mechanics from
// the "Rakennekaavio" planning artifact — draggable/resizable cards, SVG
// connection lines between related tables, grid-paper background, same
// color tokens/fonts — but each card shows the real DB rows for the current
// PTS project, one field-list per row (matching the artifact's own vertical
// field-row style) rather than a cramped spreadsheet-style table.
const STATUS = {
  existing: { color: 'var(--existing)', bg: 'var(--existing-bg)', label: 'nykyinen', style: 'solid' },
  new: { color: 'var(--new)', bg: 'var(--new-bg)', label: 'uusi', style: 'solid' },
  computed: { color: 'var(--computed)', bg: 'var(--computed-bg)', label: 'johdettu', style: 'dashed' },
};

const TABLES = [
  { key: 'dynamic_pts_project', label: 'dynamic_pts_projects', status: 'existing', single: true },
  { key: 'dynamic_pts_sections', label: 'dynamic_pts_sections', status: 'existing' },
  { key: 'kuntoarviovalikko', label: 'kuntoarviovalikko', status: 'new' },
  { key: 'tarkastuskohde', label: 'tarkastuskohde', status: 'new' },
  // Placed right after tarkastuskohde (its FK target), before the
  // typically-larger poranayte/testi/peitepaksuus_arvo tables — the
  // masonry layout below fills columns in array order, so a table placed
  // after those would only get laid out once they'd already stacked the
  // columns tall, landing it far below the fold.
  { key: 'testi_caption', label: 'testi_caption', status: 'new' },
  { key: 'rakenneosa', label: 'rakenneosa', status: 'new' },
  { key: 'poranayte', label: 'poranayte', status: 'new' },
  { key: 'peitepaksuus', label: 'peitepaksuus', status: 'computed' },
  { key: 'peitepaksuus_arvo', label: 'peitepaksuus_arvo', status: 'new' },
  { key: 'testi', label: 'testi', status: 'new' },
];

// FK edges, hardcoded from the actual schema (services/julkisivuService.js /
// models/*.js) — {from: table with the FK column, to: table it references, via: column name}.
const EDGES = [
  { from: 'dynamic_pts_sections', to: 'dynamic_pts_project', via: 'id_dynamic_pts_project' },
  { from: 'kuntoarviovalikko', to: 'dynamic_pts_project', via: 'id_pts_project' },
  { from: 'kuntoarviovalikko', to: 'dynamic_pts_sections', via: 'id_dynamic_pts_section' },
  { from: 'tarkastuskohde', to: 'kuntoarviovalikko', via: 'id_kuntoarviovalikko' },
  { from: 'rakenneosa', to: 'tarkastuskohde', via: 'id_tarkastuskohde' },
  { from: 'poranayte', to: 'rakenneosa', via: 'id_rakenneosa' },
  { from: 'peitepaksuus', to: 'rakenneosa', via: 'id_rakenneosa' },
  { from: 'peitepaksuus_arvo', to: 'peitepaksuus', via: 'id_peitepaksuus' },
  { from: 'testi', to: 'poranayte', via: 'id_poranayte' },
  { from: 'testi_caption', to: 'tarkastuskohde', via: 'id_tarkastuskohde' },
];

const CARD_W = 400, COLS = 3, GAP = 60, CANVAS_W = 2500, CANVAS_H = 2400;

function fmt(v) {
  if (v === null || v === undefined) return '∅';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// Masonry-ish initial layout: place each card at the top of whichever
// column is currently shortest, using a rough height estimate from its row
// count. Cards are draggable afterwards, this is just a starting point.
function layoutCards(dump) {
  const colHeights = Array(COLS).fill(24);
  const pos = {};
  TABLES.forEach(def => {
    const raw = dump[def.key];
    const rowCount = def.single ? (raw ? 1 : 0) : (raw?.length ?? 0);
    const colCount = dump.columns?.[def.key]?.length ?? 6;
    const estHeight = 46 + Math.max(rowCount, 1) * (colCount * 17 + 18) + 20;
    let col = 0;
    for (let i = 1; i < COLS; i++) if (colHeights[i] < colHeights[col]) col = i;
    pos[def.key] = { x: 24 + col * (CARD_W + GAP), y: colHeights[col], w: CARD_W };
    colHeights[col] += estHeight + GAP;
  });
  return pos;
}

const STYLE = `
  .dbv-root{
    --bg:#eef1f6; --bg-grid: rgba(15,23,42,.07);
    --surface:#ffffff; --surface-2:#f5f8fb;
    --border:#d7dfea; --border-strong:#b9c5d6;
    --text:#131e2c; --text-dim:#5c6c81; --text-faint:#93a1b5;
    --existing:#5b6b82; --existing-bg:#eef1f6;
    --new:#0d8f83; --new-bg:#e4f5f2;
    --computed:#b0650f; --computed-bg:#fbf0df;
    --accent:#0ea5b7; --line: rgba(19,30,44,.32); --line-strong:#0ea5b7;
    --shadow: 0 1px 2px rgba(15,23,42,.05), 0 8px 24px rgba(15,23,42,.09);
    --font-ui: 'Manrope', system-ui, sans-serif;
    --font-mono: 'IBM Plex Mono', ui-monospace, monospace;
    position:fixed; inset:0; display:flex; flex-direction:column;
    background:var(--bg); color:var(--text); font-family:var(--font-ui);
  }
  .dbv-toolbar{
    flex:0 0 auto; display:flex; align-items:center; gap:14px; padding:10px 16px;
    background:var(--surface); border-bottom:1px solid var(--border); box-shadow:var(--shadow); flex-wrap:wrap; z-index:5;
  }
  .dbv-toolbar h1{ font-size:15px; font-weight:800; margin:0; white-space:nowrap; }
  .dbv-toolbar h1 span{ color:var(--accent); }
  .dbv-legend{ display:flex; gap:10px; align-items:center; font-size:11.5px; color:var(--text-dim); }
  .dbv-legend .dot{ display:inline-flex; align-items:center; gap:5px; }
  .dbv-legend i{ width:9px; height:9px; border-radius:3px; display:inline-block; }
  .dbv-btn{
    display:inline-flex; align-items:center; gap:6px; border:1px solid var(--border-strong);
    background:var(--surface-2); color:var(--text); border-radius:8px; padding:7px 12px;
    font-size:12.5px; font-weight:700; cursor:pointer; white-space:nowrap;
  }
  .dbv-btn:hover{ border-color:var(--accent); }
  .dbv-stats{ margin-left:auto; font-size:11.5px; color:var(--text-dim); font-family:var(--font-mono); white-space:nowrap; }
  .dbv-viewport{ position:relative; flex:1 1 auto; overflow:auto; }
  .dbv-canvas{
    position:relative; width:${CANVAS_W}px; height:${CANVAS_H}px;
    background-image:
      linear-gradient(var(--bg-grid) 1px, transparent 1px),
      linear-gradient(90deg, var(--bg-grid) 1px, transparent 1px);
    background-size:28px 28px;
  }
  svg.dbv-edges{ position:absolute; inset:0; width:100%; height:100%; pointer-events:none; z-index:1; }
  .dbv-edge-path{ fill:none; stroke:var(--line); stroke-width:1.6; }
  .dbv-edge-label{
    position:absolute; transform:translate(-50%,-50%); background:var(--surface);
    border:1px solid var(--border-strong); border-radius:20px; padding:2px 8px;
    font-family:var(--font-mono); font-size:10.5px; font-weight:700; color:var(--text-dim);
    box-shadow:var(--shadow); white-space:nowrap; z-index:2;
  }
  .dbv-card{
    position:absolute; background:var(--surface); border:1px solid var(--border); border-radius:12px;
    box-shadow:var(--shadow); overflow:hidden; resize:horizontal; min-width:280px; max-width:900px; z-index:6;
  }
  .dbv-card.dragging{ box-shadow:0 4px 8px rgba(15,23,42,.1), 0 16px 40px rgba(15,23,42,.18); z-index:20; }
  .dbv-card-head{
    display:flex; align-items:center; gap:8px; padding:9px 12px; border-bottom:1px solid var(--border); cursor:grab;
  }
  .dbv-card-head:active{ cursor:grabbing; }
  .dbv-card-name{ font-family:var(--font-mono); font-weight:700; font-size:13px; color:var(--text); }
  .dbv-status-pill{
    font-size:9.5px; font-weight:800; text-transform:uppercase; letter-spacing:.03em;
    border-radius:6px; padding:2px 6px; border:1px solid var(--border-strong);
  }
  .dbv-count{ font-family:var(--font-mono); font-size:11px; color:var(--text-dim); }
  .dbv-records-wrap{ max-height:380px; overflow-y:auto; }
  .dbv-empty{ padding:10px 12px; color:var(--text-faint); font-style:italic; font-size:11.5px; }
  .dbv-record{ padding:7px 12px; border-bottom:1px solid var(--surface-2); }
  .dbv-record:last-child{ border-bottom:none; }
  .dbv-record-id{ font-family:var(--font-mono); font-weight:800; font-size:10px; color:var(--new); margin-bottom:4px; text-transform:uppercase; letter-spacing:.03em; }
  .dbv-field-row{ display:flex; align-items:baseline; gap:10px; padding:1.5px 0; font-family:var(--font-mono); font-size:11.5px; }
  .dbv-field-name{ color:var(--text-dim); flex:1 1 auto; }
  .dbv-field-name.fk{ color:var(--accent); }
  .dbv-field-val{ color:var(--text); text-align:right; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:55%; }
  .dbv-field-val.null{ color:var(--text-faint); }
`;

function edgeAnchors(a, b) {
  const dx = (b.x + b.w / 2) - (a.x + a.w / 2);
  const dy = (b.y + b.h / 2) - (a.y + a.h / 2);
  let p1, p2;
  if (Math.abs(dx) >= Math.abs(dy)) {
    p1 = dx >= 0 ? { x: a.x + a.w, y: a.y + a.h / 2 } : { x: a.x, y: a.y + a.h / 2 };
    p2 = dx >= 0 ? { x: b.x, y: b.y + b.h / 2 } : { x: b.x + b.w, y: b.y + b.h / 2 };
  } else {
    p1 = dy >= 0 ? { x: a.x + a.w / 2, y: a.y + a.h } : { x: a.x + a.w / 2, y: a.y };
    p2 = dy >= 0 ? { x: b.x + b.w / 2, y: b.y } : { x: b.x + b.w / 2, y: b.y + b.h };
  }
  return [p1, p2];
}

function TableCard({ def, columns, rows, pos, onDrag, onResize, registerCard, redrawEdges }) {
  const elRef = useRef(null);
  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const st = STATUS[def.status];

  const onPointerDown = useCallback((e) => {
    if (!e.target.closest('.dbv-card-head')) return;
    if (e.target.closest('input,select,button,textarea')) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y, moved: false };
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [pos.x, pos.y]);

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true;
    if (!dragRef.current.moved) return;
    const el = elRef.current;
    if (el) {
      el.style.left = Math.max(0, dragRef.current.origX + dx) + 'px';
      el.style.top = Math.max(0, dragRef.current.origY + dy) + 'px';
      redrawEdges();
    }
  }, [redrawEdges]);

  const onPointerUp = useCallback(() => {
    if (!dragRef.current) return;
    setDragging(false);
    if (dragRef.current.moved && elRef.current) {
      onDrag(def.key, parseFloat(elRef.current.style.left) || 0, parseFloat(elRef.current.style.top) || 0);
    }
    dragRef.current = null;
  }, [def.key, onDrag]);

  // Native CSS resize:horizontal gives a free drag handle; ResizeObserver is
  // the only reliable way to hear the resulting width/height change. Read
  // offsetWidth (border-box) rather than entries[0].contentRect
  // (content-box) — comparing those against each other causes a
  // shrink-feedback loop where each cycle measures a few px smaller.
  const roRef = useRef(null);
  const cardRefCb = useCallback((node) => {
    elRef.current = node;
    registerCard(def.key, node);
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (node && typeof ResizeObserver !== 'undefined') {
      roRef.current = new ResizeObserver(() => {
        const w = node.offsetWidth;
        redrawEdges();
        if (Math.abs(w - pos.w) < 1) return;
        onResize(def.key, w);
      });
      roRef.current.observe(node);
    }
  }, [def.key, onResize, pos.w, registerCard, redrawEdges]);

  return (
    <div ref={cardRefCb} className={`dbv-card${dragging ? ' dragging' : ''}`}
      style={{ left: pos.x, top: pos.y, width: pos.w, borderTop: `3px ${st.style} ${st.color}` }}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}>
      <div className="dbv-card-head">
        <span className="dbv-card-name">{def.label}</span>
        <span className="dbv-status-pill" style={{ color: st.color, background: st.bg }}>{st.label}</span>
        <span className="dbv-count" style={{ marginLeft: 'auto' }}>{rows.length} riviä</span>
      </div>
      <div className="dbv-records-wrap">
        {rows.length === 0 ? <div className="dbv-empty">ei rivejä</div> : rows.map((r, i) => (
          <div className="dbv-record" key={r.id ?? i}>
            <div className="dbv-record-id">#{r.id ?? i + 1}</div>
            {columns.filter(c => c !== 'id').map(c => (
              <div className="dbv-field-row" key={c}>
                <span className={`dbv-field-name${c.startsWith('id_') ? ' fk' : ''}`}>{c}</span>
                <span className={`dbv-field-val${r[c] === null || r[c] === undefined ? ' null' : ''}`} title={fmt(r[c])}>{fmt(r[c])}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DbViewerPage() {
  const { ptsProjectId } = useParams();
  const { dump, loading, error, refresh } = useDbDebugV2(ptsProjectId, true);
  const [positions, setPositions] = useState(null);
  const cardNodes = useRef({});
  const edgeEls = useRef({});
  const svgRef = useRef(null);
  const labelLayerRef = useRef(null);

  const layout = useMemo(() => (dump ? layoutCards(dump) : null), [dump]);
  const pos = positions || layout;

  const registerCard = useCallback((key, node) => {
    if (node) cardNodes.current[key] = node; else delete cardNodes.current[key];
  }, []);

  const redrawEdges = useCallback(() => {
    const svg = svgRef.current, labelLayer = labelLayerRef.current;
    if (!svg || !labelLayer) return;
    EDGES.forEach(edge => {
      const fromEl = cardNodes.current[edge.from], toEl = cardNodes.current[edge.to];
      const edgeKey = edge.from + '->' + edge.to;
      let path = edgeEls.current[edgeKey];
      let label = edgeEls.current[edgeKey + '_label'];
      if (!fromEl || !toEl) {
        if (path) path.setAttribute('d', '');
        if (label) label.style.display = 'none';
        return;
      }
      if (!path) {
        path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('class', 'dbv-edge-path');
        path.setAttribute('marker-end', 'url(#dbv-arrow)');
        svg.appendChild(path);
        edgeEls.current[edgeKey] = path;
      }
      if (!label) {
        label = document.createElement('div');
        label.className = 'dbv-edge-label';
        labelLayer.appendChild(label);
        edgeEls.current[edgeKey + '_label'] = label;
      }
      const a = { x: fromEl.offsetLeft, y: fromEl.offsetTop, w: fromEl.offsetWidth, h: fromEl.offsetHeight };
      const b = { x: toEl.offsetLeft, y: toEl.offsetTop, w: toEl.offsetWidth, h: toEl.offsetHeight };
      const [p1, p2] = edgeAnchors(a, b);
      const cx1 = p1.x + (p2.x - p1.x) * 0.5, cx2 = cx1;
      path.setAttribute('d', `M ${p1.x},${p1.y} C ${cx1},${p1.y} ${cx2},${p2.y} ${p2.x},${p2.y}`);
      label.style.display = '';
      label.style.left = ((p1.x + p2.x) / 2) + 'px';
      label.style.top = ((p1.y + p2.y) / 2) + 'px';
      label.textContent = edge.via;
    });
  }, []);

  useEffect(() => { redrawEdges(); }, [pos, redrawEdges]);

  const onDrag = useCallback((key, x, y) => {
    setPositions(prev => ({ ...(prev || layout), [key]: { ...(prev || layout)[key], x, y } }));
  }, [layout]);
  const onResize = useCallback((key, w) => {
    setPositions(prev => ({ ...(prev || layout), [key]: { ...(prev || layout)[key], w } }));
  }, [layout]);

  const tCount = TABLES.reduce((n, def) => n + (dump ? (def.single ? (dump[def.key] ? 1 : 0) : (dump[def.key]?.length ?? 0)) : 0), 0);

  return (
    <div className="dbv-root">
      <style>{STYLE}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Manrope:wght@500;700;800&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap" />

      <div className="dbv-toolbar">
        <h1>DB-taulut <span>· {dump?.dynamic_pts_project?.name ?? `PTS ${ptsProjectId}`}</span></h1>
        <div className="dbv-legend">
          {Object.entries(STATUS).map(([k, s]) => (
            <span className="dot" key={k}><i style={{ background: s.color }} />{s.label}</span>
          ))}
        </div>
        <button type="button" className="dbv-btn" onClick={() => { setPositions(null); refresh(); }} disabled={loading}>
          {loading ? 'Ladataan…' : '↻ Päivitä'}
        </button>
        <div className="dbv-stats">{tCount} riviä yhteensä · raahaa otsikosta, venytä reunasta</div>
      </div>

      <div className="dbv-viewport">
        <div className="dbv-canvas">
          <svg ref={svgRef} className="dbv-edges">
            <defs>
              <marker id="dbv-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 z" fill="var(--line)" />
              </marker>
            </defs>
          </svg>
          <div ref={labelLayerRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />

          {error && <div style={{ position: 'absolute', top: 16, left: 16, color: '#c8483a', background: '#fbeae8', border: '1px solid #e9c78f', borderRadius: 8, padding: '10px 14px', fontSize: 12.5 }}>{error}</div>}
          {!dump && !error && <div style={{ position: 'absolute', top: 16, left: 16, color: 'var(--text-dim)' }}>Ladataan…</div>}
          {dump && pos && TABLES.map(def => {
            const raw = dump[def.key];
            const rows = def.single ? (raw ? [raw] : []) : (raw ?? []);
            return (
              <TableCard key={def.key} def={def} columns={dump.columns?.[def.key] ?? Object.keys(rows[0] ?? {})}
                rows={rows} pos={pos[def.key]} onDrag={onDrag} onResize={onResize}
                registerCard={registerCard} redrawEdges={redrawEdges} />
            );
          })}
        </div>
      </div>
    </div>
  );
}
